// ============================================================================
// AXIOM — Ops: the operator-token catalogue
// ----------------------------------------------------------------------------
// Every token is a *pure left-to-right pipeline stage*: `x` flows in, a BigNum
// flows out. There is no precedence, no implicit grouping — slot order IS the
// order of operations, which is the entire game (DESIGN §2).
//
//   (x + 5)^2   ==  [add 5][pow 2]
//   x^2 + 5     ==  [pow 2][add 5]
//
// `ctx.meta` is a scratch object the pipeline carries so a token can leave a
// note for tokens downstream (`meta.imaginary`, `meta.divZero`, `meta.addTotal`).
// Perks are read through `ctx.perks` (a Set of perk id strings).
// ============================================================================

import BigNum from './BigNum.js';
import { PHI, E, PI, nearestPrime, digitCount } from './NumberTheory.js';

export const OpKind = {
  ADD: 'add', SUB: 'sub', MUL: 'mul', DIV: 'div', POW: 'pow',
  ROOT: 'root', LOG: 'log', FACT: 'fact', SIGMA: 'sigma', INTEGRAL: 'integral',
  DERIV: 'deriv', TETRA: 'tetra', LIM: 'lim', MOD: 'mod', IMAG: 'imag', ABS: 'abs',
  SIN: 'sin', CONST: 'const',
};

// ---------------------------------------------------------------------- tuning
// Exposed so balance.test.mjs can reason about (and regression-lock) the curve.
export const TUNING = {
  /** `!` operates on the digit count, not the value — see OPS.fact. */
  FACT_MIN_DIGITS: 2,
  /** `↑↑n` raises the *exponent* to this power per extra height. */
  TETRA_EXP_PER_LEVEL: 0.3,
  TETRA_MIN_GAIN: 1.45,
  /** `Σ` can never do better than multiplying by (1 + window). */
  SIGMA_WINDOW: 5,
  /** `lim` divergence: L -> L*LIM_GAIN + LIM_FLAT. */
  LIM_GAIN: 2.0,
  LIM_FLAT: 2.0,
  LIM_LHOPITAL_GAIN: 2.6,
  /** `sin` amplitude; mean stays 1.0 so it is a variance token, not a damage token. */
  SIN_AMPLITUDE: 0.9,
  SIN_FLOOR: 0.06,
  /** `i` halves real damage (DESIGN §2). */
  IMAG_REAL_PENALTY: 0.5,
  /** Above this exponent the trailing digits are not representable, so `mod` */
  /** declares alignment instead of snapping. */
  MOD_EXACT_MAX_EXP: 14,
};

// --------------------------------------------------------------------- helpers

function num(v) {
  const n = typeof v === 'number' ? v : (v && typeof v.toNumber === 'function' ? v.toNumber() : Number(v));
  return Number.isFinite(n) ? n : 0;
}

function big(v) {
  return v instanceof BigNum ? v : BigNum.from(v);
}

/**
 * An operand for display. A negative one is bracketed and typeset with a real
 * U+2212 minus, so `x · (−3)` reads as a signed factor rather than as the
 * `x · -3` we used to emit — which mixes a typeset operator glyph with an ASCII
 * hyphen operand and is genuinely ambiguous about where the subtraction is.
 */
function opnd(v) {
  const n = num(v);
  return n < 0 ? `(−${-n})` : `${n}`;
}

// One-entry nearestPrime cache for mod.apply — see the comment at its use site.
let _primeMemoIn = -1, _primeMemoOut = 0;

/** `rand` may be a core/Rand instance or a bare () => [0,1) function. */
function rnd(r) {
  if (!r) return Math.random();
  if (typeof r === 'function') return r();
  if (typeof r.next === 'function') return r.next();
  return Math.random();
}
function rpick(r, arr) { return arr[Math.min(arr.length - 1, Math.floor(rnd(r) * arr.length))]; }

function hasPerk(ctx, id) {
  const p = ctx && ctx.perks;
  return !!(p && typeof p.has === 'function' && p.has(id));
}

function M(ctx) {
  if (!ctx) return {};
  if (!ctx.meta) ctx.meta = {};
  return ctx.meta;
}

/** Banach-Tarski: the token you picked up split in two. Tier I/II duplicate in place. */
function banachScale(ctx, n, multiplicative) {
  if (!hasPerk(ctx, 'banach')) return n;
  // Duplicating a token doubles its MAGNITUDE; it must not launder its sign.
  // A bare n*n turns ×(−3) into ×+9 with nothing on screen to explain it, and
  // opMatrix does not apply banachScale at all — so the Matrix Golem gate would
  // read det = −3 while the bullet multiplied by +9.
  return multiplicative ? Math.sign(n) * n * n : n * 2;
}

const SUB_SUP = { 0: '₀', 1: '₁', 2: '₂', 3: '₃', 4: '₄', 5: '₅', 6: '₆', 7: '₇', 8: '₈', 9: '₉', '-': '₋' };
const SUP = { 0: '⁰', 1: '¹', 2: '²', 3: '³', 4: '⁴', 5: '⁵', 6: '⁶', 7: '⁷', 8: '⁸', 9: '⁹', '-': '⁻' };
function sub(n) { return String(n).split('').map((c) => SUB_SUP[c] ?? c).join(''); }
function sup(n) { return String(n).split('').map((c) => SUP[c] ?? c).join(''); }

// ------------------------------------------------------------------- the table
//
// tier   colour family        tokens
//  1     cyan                 + −
//  2     green                × ÷ and the constants π e φ
//  3     amber / orange       ^ √ log  |x|
//  4     violet / magenta     ! Σ ∫ d/dt  mod  i  sin
//  5     red / gold           ↑↑ lim
//
export const OPS = {

  // ------------------------------------------------------------------ tier I
  add: {
    id: 'add', kind: OpKind.ADD, tier: 1, symbol: '+', color: 0x53e0ff,
    label: 'Addition', labelJa: '加算',
    desc: 'x + n. Enormous while x is small, meaningless once it is not.',
    descJa: 'x + n。序盤は最強、桁が増えるとほぼ無意味になる。',
    minWave: 0, weight: 1.0, order: 10, rate: 1.0,
    rollValue(rand, wave) { return Math.max(2, Math.round(3 + rnd(rand) * 4 + (wave || 0) * 0.3)); },
    latex(v) { return `+ ${num(v)}`; },
    latexWrap(inner, v) { return `${inner} + ${num(v)}`; },
    apply(x, v, ctx) {
      const n = banachScale(ctx, num(v), false);
      const m = M(ctx);
      m.addTotal = (m.addTotal || 0) + n;
      return big(x).add(BigNum.from(n));
    },
  },

  sub: {
    id: 'sub', kind: OpKind.SUB, tier: 1, symbol: '−', color: 0x2f8fbf,
    label: 'Subtraction', labelJa: '減算',
    desc: 'x − n. A curse — unless you own lim, or Ramanujan flips its sign.',
    descJa: 'x − n。呪い。だが lim やラマヌジャン和と組むと化ける。',
    minWave: 0, weight: 0.55, order: 92, rate: 1.02,
    rollValue(rand, wave) { return Math.max(1, Math.round(2 + rnd(rand) * 3 + (wave || 0) * 0.22)); },
    latex(v) { return `− ${num(v)}`; },
    latexWrap(inner, v) { return `${inner} − ${num(v)}`; },
    apply(x, v, ctx) {
      const n = banachScale(ctx, num(v), false);
      const m = M(ctx);
      // 1+2+3+... = −1/12 : the subtraction token runs backwards.
      if (hasPerk(ctx, 'ramanujan')) {
        m.addTotal = (m.addTotal || 0) + n;
        m.ramanujan = true;
        return big(x).add(BigNum.from(n * (13 / 12)));
      }
      m.negTouched = true;
      const out = big(x).sub(BigNum.from(n));
      if (out.isNeg()) m.negative = true;
      return out;
    },
  },

  // ----------------------------------------------------------------- tier II
  mul: {
    id: 'mul', kind: OpKind.MUL, tier: 2, symbol: '×', color: 0x6ef07a,
    label: 'Multiplication', labelJa: '乗算',
    desc: 'x · n. The mid-game backbone. Wants every addition to its left.',
    descJa: 'x · n。中盤の背骨。加算は必ずこの左に置くこと。',
    minWave: 2, weight: 1.0, order: 20, rate: 0.97,
    rollValue(rand, wave) { return 2 + Math.floor(rnd(rand) * 3) + Math.floor((wave || 0) / 26); },
    latex(v) { return `· ${opnd(v)}`; },
    latexWrap(inner, v) { return `${inner} · ${opnd(v)}`; },
    apply(x, v, ctx) {
      const k = banachScale(ctx, num(v) || 1, true);
      const m = M(ctx);
      let out = big(x).mul(BigNum.from(k));
      // Distributive Law: (a+b)·c also re-applies the additive terms after the product.
      if (m.addTotal && hasPerk(ctx, 'distributive')) {
        out = out.add(BigNum.from(m.addTotal * k));
        m.distributed = true;
      }
      return out;
    },
  },

  div: {
    id: 'div', kind: OpKind.DIV, tier: 2, symbol: '÷', color: 0x3fa050,
    label: 'Division', labelJa: '除算',
    desc: 'x / n. A curse, and lim eats curses for breakfast.',
    descJa: 'x / n。呪い。lim の餌。',
    minWave: 2, weight: 0.5, order: 6, rate: 1.05,
    rollValue(rand) { return 2 + Math.floor(rnd(rand) * 2); },
    latex(v) { return `÷ ${opnd(v)}`; },
    latexWrap(inner, v) { return `${inner} / ${opnd(v)}`; },
    apply(x, v, ctx) {
      const d = banachScale(ctx, num(v), true);
      const m = M(ctx);
      m.divTouched = true;
      if (d === 0) {
        m.divZero = true;
        // L'Hopital: 0/0 and inf/inf resolve to the ratio of derivatives.
        if (hasPerk(ctx, 'lhopital')) {
          m.lhopital = true;
          const L = Math.max(1, big(x).logAbs10());
          return BigNum.fromLog10(L * 2 + 1);
        }
        if (big(x).isZero()) { m.undefinedForm = true; return BigNum.ONE; }
        return big(x);                        // pass through; Formula raises the NaN-bullet flag
      }
      return big(x).div(BigNum.from(d));
    },
  },

  // ---------------------------------------------------------------- tier III
  pow: {
    id: 'pow', kind: OpKind.POW, tier: 3, symbol: '^', color: 0xffb347,
    label: 'Power', labelJa: '冪乗',
    desc: 'x^n. Doubles your digit count. Everything additive belongs to its left.',
    descJa: 'x^n。桁数がそのまま n 倍になる。本命。',
    minWave: 8, weight: 0.62, order: 50, rate: 0.9,
    rollValue(rand, wave) {
      const r = rnd(rand);
      if ((wave || 0) >= 30 && r < 0.18) return 3;
      return 2;
    },
    latex(v) { return `^${num(v)}`; },
    latexWrap(inner, v) { return `${inner}${sup(num(v))}`; },
    apply(x, v, ctx) {
      const n = num(v) || 2;
      const m = M(ctx);
      const b = big(x);
      let out;
      if (b.isNeg()) {
        m.negative = true;
        out = b.abs().pow(n);
        if (Number.isInteger(n) && Math.abs(n % 2) === 1) out = out.neg();
      } else {
        out = b.pow(n);
      }
      // Fermat's Last Theorem: ^n with n>=3 hits three times as hard, once per build.
      if (n >= 3 && hasPerk(ctx, 'fermat') && !m.fermatUsed) {
        m.fermatUsed = true;
        out = out.mul(BigNum.from(3));
      }
      return out;
    },
  },

  root: {
    id: 'root', kind: OpKind.ROOT, tier: 3, symbol: '√', color: 0xff8c42,
    label: 'Root', labelJa: '冪根',
    desc: 'ⁿ√x. Halves your digits — but a negative x under it births i.',
    descJa: 'ⁿ√x。桁が半分に。負数に掛けると虚数化する。',
    minWave: 6, weight: 0.45, order: 5, rate: 1.02,
    rollValue(rand) { return rnd(rand) < 0.15 ? 3 : 2; },
    latex(v) { const n = num(v); return n === 2 ? '√' : `${n}√`; },
    latexWrap(inner, v) { const n = num(v); return n === 2 ? `√(${inner})` : `${sup(n)}√(${inner})`; },
    apply(x, v, ctx) {
      const n = num(v) || 2;
      const m = M(ctx);
      const b = big(x);
      // A negative radicand really does birth i — but record WHERE the sign came
      // from. `abs` pays an imaginary term back at ×2, so without provenance a
      // player-minted ×(−3) plus a √ they already own is a free doubling
      // (measured exactly 2.0000×). Also: do not clear a `negative` marker this
      // token did not create; that belongs to whoever set it.
      if (b.isNeg()) { m.imaginary = true; m.imagFromSign = true; }
      return b.abs().root(n);
    },
  },

  log: {
    id: 'log', kind: OpKind.LOG, tier: 3, symbol: 'log', color: 0xd98f2b,
    label: 'Logarithm', labelJa: '対数',
    desc: 'log_b x. Brutal compression — and the only clean answer to a Zeno Crawler.',
    descJa: 'log_b x。大幅縮小。だが Zeno 敵には特効。',
    minWave: 6, weight: 0.3, order: 1, rate: 1.05,
    rollValue(rand) { return rnd(rand) < 0.25 ? 10 : 2; },
    latex(v) { return `log${sub(num(v))}`; },
    latexWrap(inner, v) { return `log${sub(num(v))}(${inner})`; },
    apply(x, v, ctx) {
      const b = num(v) || 2;
      const m = M(ctx);
      m.logged = true;
      const val = big(x).abs();
      if (val.lte(BigNum.ONE)) return BigNum.ONE;
      const out = val.logBase(b);
      return out.lte(BigNum.ONE) ? BigNum.ONE : out;
    },
  },

  abs: {
    id: 'abs', kind: OpKind.ABS, tier: 3, symbol: '|x|', color: 0xb8c0cc,
    label: 'Absolute Value', labelJa: '絶対値',
    desc: '|x|. Insurance against − and i: collapses an imaginary term back to its magnitude.',
    descJa: '|x|。i と − の保険。虚数を実数の大きさに畳み戻す。',
    minWave: 4, weight: 0.4, order: 88, rate: 1.0, special: true,
    rollValue() { return 1; },
    latex() { return '| |'; },
    latexWrap(inner) { return `|${inner}|`; },
    apply(x, v, ctx) {
      const m = M(ctx);
      let out = big(x).abs();
      if (m.imaginary) {
        // |a + bi| — the imaginary component comes back as real magnitude. Only
        // a GENUINE imaginary term pays: one conjured by √ over a sign the
        // player minted himself is just a minus sign, and paying ×2 for it made
        // conjugation a free doubling for anyone carrying a √.
        if (!m.imagFromSign) { m.wasImaginary = true; out = out.mul(BigNum.from(2)); }
        m.imaginary = false;
        m.imagFromSign = false;
      }
      m.negative = false;
      return out;
    },
  },

  // ----------------------------------------------------------------- tier IV
  fact: {
    id: 'fact', kind: OpKind.FACT, tier: 4, symbol: '!', color: 0xff5fd0,
    label: 'Factorial', labelJa: '階乗',
    desc: 'x · Γ(d+1) where d is your digit count. Explosive, −60% fire rate.',
    descJa: 'x · Γ(桁数+1)。爆発的だが発射レート −60%。',
    minWave: 34, weight: 0.42, order: 60, rate: 0.4,
    rollValue() { return 1; },
    latex() { return '!'; },
    latexWrap(inner) { return `(${inner})!`; },
    apply(x, v, ctx) {
      // Γ(x+1) on the raw value is not a token, it is a win button: at x=1e15 it
      // returns 1e(1.4e16) and the wave curve stops existing. So `!` takes the
      // factorial of the *order of magnitude* — log10(result) ≈ L·(1+log10 L),
      // i.e. a little stronger than ^2 and strictly weaker than ↑↑. Still the
      // real Γ, just applied to the digit count.
      const b = big(x);
      const d = Math.max(TUNING.FACT_MIN_DIGITS, digitCount(b));
      const g = BigNum.from(d).factorial();
      const m = M(ctx);
      m.factorialDigits = d;
      const out = b.abs().mul(g);
      return b.isNeg() ? out.neg() : out;
    },
  },

  sigma: {
    id: 'sigma', kind: OpKind.SIGMA, tier: 4, symbol: 'Σ', color: 0xc77dff,
    label: 'Summation', labelJa: '総和',
    desc: 'x + Σ(last 5 hits), capped at ×6. The core of every rapid-fire build.',
    descJa: 'x + 直近5発の合計（最大×6）。連射ビルドの核。',
    minWave: 30, weight: 0.55, order: 30, rate: 1.1,
    rollValue() { return TUNING.SIGMA_WINDOW; },
    latex(v) { return `+ Σ${sub(num(v))}`; },
    latexWrap(inner, v) { return `${inner} + Σ${sub(num(v))}`; },
    apply(x, v, ctx) {
      const b = big(x);
      const k = Math.max(1, Math.round(num(v) || TUNING.SIGMA_WINDOW));
      const hits = (ctx && ctx.recentHits) || [];
      let s = BigNum.ZERO;
      for (let i = Math.max(0, hits.length - k); i < hits.length; i++) s = s.add(big(hits[i]));
      if (hasPerk(ctx, 'ramanujan')) s = s.add(b.mul(BigNum.from(1 / 12)));
      const summed = b.add(s);
      // Hard ceiling: without it, Σ placed in slot 0 feeds its own output back
      // through the whole pipeline and the run diverges over ~20 shots.
      const ceiling = b.abs().mul(BigNum.from(1 + k));
      M(ctx).sigmaHits = Math.min(k, hits.length);
      // The ceiling is built from |b|, so on a negative running value it sits on
      // the wrong side of zero: `summed.gt(ceiling)` was never true going down,
      // and a negative sum could be laundered into a positive one. Compare
      // magnitudes and carry the sign of the value we are capping.
      if (summed.abs().gt(ceiling)) return b.isNeg() ? ceiling.neg() : ceiling;
      return summed;
    },
  },

  integral: {
    id: 'integral', kind: OpKind.INTEGRAL, tier: 4, symbol: '∫', color: 0x9d4edd,
    label: 'Integral', labelJa: '積分',
    desc: '∫x dt. Hold the trigger: damage integrates over trigger-hold time.',
    descJa: '∫x dt。撃ちっぱなしで時間積分。長押しビーム化。',
    minWave: 30, weight: 0.5, order: 33, rate: 0.75,
    rollValue(rand) { return 0.55 + rnd(rand) * 0.3; },
    latex() { return '∫ dt'; },
    latexWrap(inner) { return `∫(${inner}) dt`; },
    apply(x, v, ctx) {
      const g = num(v) || 0.6;
      const hold = Math.max(0, Math.min(4, num((ctx && ctx.meta && ctx.meta.holdTime) ?? (ctx && ctx.holdTime) ?? 0)));
      M(ctx).integrated = hold;
      return big(x).mul(BigNum.from(1 + g * hold));
    },
  },

  deriv: {
    id: 'deriv', kind: OpKind.DERIV, tier: 4, symbol: 'd/dt', color: 0xe0aaff,
    label: 'Derivative', labelJa: '微分',
    desc: 'x · (1 + dDPS/dt). Rewards acceleration — keep speeding up.',
    descJa: 'x · (1 + DPSの変化率)。加速し続けろ。',
    minWave: 30, weight: 0.5, order: 34, rate: 1.0,
    rollValue(rand) { return 0.5 + rnd(rand) * 0.4; },
    latex() { return 'd/dt'; },
    latexWrap(inner) { return `d/dt(${inner})`; },
    apply(x, v, ctx) {
      const g = num(v) || 0.6;
      const m = M(ctx);
      let dd = num(m.dpsDelta ?? (ctx && ctx.dpsDelta) ?? 0);
      if (m.dOsc) dd += Math.abs(m.dOsc);            // sin gives d/dt something to bite
      dd = Math.max(-0.5, Math.min(4, dd));
      m.derived = dd;
      return big(x).mul(BigNum.from(Math.max(0.2, 1 + g * dd)));
    },
  },

  mod: {
    id: 'mod', kind: OpKind.MOD, tier: 4, symbol: 'mod', color: 0xffe066,
    label: 'Modulo', labelJa: '剰余',
    desc: 'Shapes damage down onto a multiple of n. Feeds Prime Crit and cracks Modular Drones.',
    descJa: 'ダメージを n の倍数に整形する。Prime Crit の誘発と Modular Drone 対策。',
    minWave: 16, weight: 0.45, order: 80, rate: 0.95, special: true,
    rollValue(rand) { return rpick(rand, [3, 5, 7, 7, 11, 13]); },
    latex(v) { return `mod ${num(v)}`; },
    latexWrap(inner, v) { return `⌊${inner}⌋${sub(num(v))}`; },
    apply(x, v, ctx) {
      // DESIGN §4.3 wants `mod` to "整形" damage into a multiple of 7 to break a
      // Modular Drone — i.e. snap DOWN to the nearest multiple, not take the
      // remainder. Taking the remainder would delete every digit you own.
      const n = Math.max(2, Math.round(num(v) || 7));
      const m = M(ctx);
      m.modulus = n;
      const b = big(x);
      const a = b.abs();
      if (a.e > TUNING.MOD_EXACT_MAX_EXP) {
        // Trailing digits are past double precision: alignment is free, but the
        // reshaping still costs you a sliver.
        m.modAligned = n;
        return b.mul(BigNum.from(0.97));
      }
      const val = Math.floor(a.toNumber());
      let snapped = Math.max(n, val - (val % n));
      if (hasPerk(ctx, 'sieve') && rnd(ctx && ctx.rand) < 0.25) {
        // Memoized, because the pipeline input is deterministic per build: the
        // same 15-digit `val` recurs every shot, and when it sits inside a wide
        // prime gap an uncached search re-pays ~1ms of Miller-Rabin on every
        // trigger — a sustained hitch, not a one-off. One entry is enough
        // precisely because the value is pinned. Budgeted for the same reason
        // as Formula's SNAP_BUDGET: a missed snap is invisible, a slow one not.
        const want = Math.max(2, val);
        if (_primeMemoIn !== want) {
          _primeMemoIn = want;
          _primeMemoOut = nearestPrime(want, 64);
        }
        if (_primeMemoOut > 0) { snapped = _primeMemoOut; m.forcedPrime = true; }
      }
      m.modAligned = n;
      return BigNum.from(b.isNeg() ? -snapped : snapped);
    },
  },

  imag: {
    id: 'imag', kind: OpKind.IMAG, tier: 4, symbol: 'i', color: 0x7f5af0,
    label: 'Imaginary Unit', labelJa: '虚数単位',
    desc: 'x → ix. Halves real damage, but an Imaginary Wraith cannot be touched without it.',
    descJa: 'x → ix。実ダメージ半減。だが Imaginary Wraith には必須。',
    minWave: 18, weight: 0.35, order: 45, rate: 0.9, special: true,
    rollValue() { return 1; },
    latex() { return '· i'; },
    latexWrap(inner) { return `i(${inner})`; },
    apply(x, v, ctx) {
      const m = M(ctx);
      m.imaginary = true;
      return big(x).mul(BigNum.from(TUNING.IMAG_REAL_PENALTY));
    },
  },

  sin: {
    id: 'sin', kind: OpKind.SIN, tier: 4, symbol: 'sin', color: 0x00e5c0,
    label: 'Sine', labelJa: '正弦',
    desc: 'x · (1 + sin t). DPS becomes a wave. Perfect food for d/dt.',
    descJa: 'x · (1 + sin t)。波打つDPS。d/dt と最高相性。',
    minWave: 16, weight: 0.4, order: 32, rate: 1.0, special: true,
    rollValue(rand) { return 1 + rnd(rand); },
    latex() { return '(1+sin t)'; },
    latexWrap(inner) { return `${inner}(1 + sin t)`; },
    apply(x, v, ctx) {
      const w = 1.6 + (num(v) || 1) * 0.6;
      const t = num((ctx && ctx.time) || 0);
      const A = TUNING.SIN_AMPLITUDE;
      let f = 1 + A * Math.sin(t * w);
      if (f < TUNING.SIN_FLOOR) f = TUNING.SIN_FLOOR;
      const m = M(ctx);
      m.oscillator = f;
      m.dOsc = A * w * Math.cos(t * w);              // handed to d/dt
      return big(x).mul(BigNum.from(f));
    },
  },

  // ------------------------------------------------------------------ tier V
  tetra: {
    id: 'tetra', kind: OpKind.TETRA, tier: 5, symbol: '↑↑', color: 0xff3b30,
    label: 'Tetration', labelJa: 'テトレーション',
    desc: 'x↑↑n. Raises your digit count to a power. The only late-game answer.',
    descJa: 'x↑↑n。桁数そのものが冪になる。終盤はこれしか効かない。',
    minWave: 45, weight: 0.5, order: 70, rate: 0.5,
    rollValue(rand, wave) { return ((wave || 0) >= 60 && rnd(rand) < 0.15) ? 3 : 2; },
    latex(v) { return `↑↑${num(v)}`; },
    latexWrap(inner, v) { return `(${inner})↑↑${num(v)}`; },
    apply(x, v, ctx) {
      // True x↑↑2 is x^x: at x=1e15 that is 1e(1.5e16) and wave 41 never happens.
      // The token instead tetrates the EXPONENT: L -> L^(1 + k(n-1)). Growth is
      // superexponential in the value and strictly beats ^n, which is the role
      // tier V has to play, without ending the run the instant it drops.
      const n = Math.max(2, Math.min(6, num(v) || 2));
      const b = big(x);
      const m = M(ctx);
      const L = b.abs().logAbs10();
      if (L <= 0.3) return b.mul(BigNum.from(2));    // nothing to tower yet
      let L2 = Math.pow(L, 1 + TUNING.TETRA_EXP_PER_LEVEL * (n - 1));
      if (!Number.isFinite(L2)) L2 = Number.MAX_VALUE;
      if (L2 < L * TUNING.TETRA_MIN_GAIN) L2 = L * TUNING.TETRA_MIN_GAIN;
      if (n >= 3 && hasPerk(ctx, 'fermat') && !m.fermatUsed) {
        m.fermatUsed = true;
        L2 += Math.log10(3);
      }
      m.tetrated = n;
      return BigNum.fromLog10(L2, b.isNeg() ? -1 : 1);
    },
  },

  lim: {
    id: 'lim', kind: OpKind.LIM, tier: 5, symbol: 'lim', color: 0xff7a1a,
    label: 'Limit', labelJa: '極限',
    desc: 'Every ÷ and − in the chain becomes a division by zero → divergence. Curses become blessings.',
    descJa: '式中の ÷ と − を全て 0 除算に変えて発散させる。呪いを祝福に。',
    minWave: 45, weight: 0.42, order: 85, rate: 0.85,
    rollValue() { return 1; },
    latex() { return 'lim'; },
    latexWrap(inner) { return `lim(${inner})`; },
    apply(x, v, ctx) {
      const m = M(ctx);
      const b = big(x);
      // `b.isNeg()` used to count as a curse, which made lim a magnitude leak
      // rather than a curse-eater: it rebuilds the value from |b| as 10^(2L+2),
      // so merely being negative bought you decades. Measured [×3][lim] = 36 vs
      // [×−3][lim] = 90,000 at base 10, and ×2.5e8 at base 1e6. A sign is not a
      // curse — divTouched / negTouched / logged / zero still are.
      const cursed = !!(m.divZero || m.divTouched || m.negTouched || m.logged || b.isZero());
      if (!cursed) return b.mul(BigNum.from(1.2));   // nothing to diverge; small consolation
      m.divergent = true;
      const L = Math.max(1, b.abs().logAbs10());
      const gain = hasPerk(ctx, 'lhopital') ? TUNING.LIM_LHOPITAL_GAIN : TUNING.LIM_GAIN;
      const flat = hasPerk(ctx, 'lhopital') ? TUNING.LIM_FLAT + 2 : TUNING.LIM_FLAT;
      return BigNum.fromLog10(L * gain + flat);
    },
  },

  // --------------------------------------------------------------- constants
  pi: {
    id: 'pi', kind: OpKind.CONST, tier: 2, symbol: 'π', color: 0xff9ff3,
    label: 'Pi', labelJa: '円周率',
    desc: 'x · π. A clean 3.14159× that never rolls low.',
    descJa: 'x · π。低い目が出ない安定係数。',
    minWave: 4, weight: 0.3, order: 21, rate: 1.0, constant: PI,
    rollValue() { return PI; },
    latex() { return '· π'; },
    latexWrap(inner) { return `${inner} · π`; },
    apply(x, v, ctx) { return big(x).mul(BigNum.from(PI)); },
  },

  e: {
    id: 'e', kind: OpKind.CONST, tier: 2, symbol: 'e', color: 0xf5d76e,
    label: "Euler's Number", labelJa: '自然対数の底',
    desc: 'x · e. The natural coefficient — pairs with log and ∫.',
    descJa: 'x · e。log や ∫ と噛み合う自然な係数。',
    minWave: 4, weight: 0.3, order: 22, rate: 1.0, constant: E,
    rollValue() { return E; },
    latex() { return '· e'; },
    latexWrap(inner) { return `${inner} · e`; },
    apply(x, v, ctx) { return big(x).mul(BigNum.from(E)); },
  },

  phi: {
    id: 'phi', kind: OpKind.CONST, tier: 2, symbol: 'φ', color: 0xffd700,
    label: 'Golden Ratio', labelJa: '黄金比',
    desc: 'x · φ, and every φ you carry lengthens the Fibonacci Chain.',
    descJa: 'x · φ。積むほど Fibonacci Chain が伸びる。',
    minWave: 4, weight: 0.34, order: 23, rate: 1.0, constant: PHI,
    rollValue() { return PHI; },
    latex() { return '· φ'; },
    latexWrap(inner) { return `${inner} · φ`; },
    apply(x, v, ctx) {
      const m = M(ctx);
      m.phiStacks = (m.phiStacks || 0) + 1;
      const k = hasPerk(ctx, 'phi_golden') ? PHI * PHI : PHI;
      return big(x).mul(BigNum.from(k));
    },
  },
};

export const OP_IDS = Object.keys(OPS);
export const OP_LIST = OP_IDS.map((k) => OPS[k]);

// Fill in defaults so a consumer never has to null-check a field.
for (const id of OP_IDS) {
  const op = OPS[id];
  if (op.special === undefined) op.special = false;
  if (op.constant === undefined) op.constant = null;
  if (op.labelJa === undefined) op.labelJa = op.label;
  if (op.descJa === undefined) op.descJa = op.desc;
}

// ------------------------------------------------------- Matrix Golem support
//
// EXTENSION (DESIGN §4.3 needs it, ARCHITECTURE.md does not name it): every
// token is also a 2×2 linear map, so a build has a matrix and a determinant.
// `lim`, `mod` and `|x|` are *reflections* (det = −1); everything else preserves
// orientation. An odd number of reflections therefore drives det below zero,
// which is exactly the Matrix Golem's invulnerability condition — and it is a
// puzzle the player can actually reason about ("drop one ⌊⌋ or add another").
// det(AB) = det(A)det(B), so the determinant is order-independent (as it must
// be); the matrix ITSELF is not, so it stays useful for order-flavoured effects.

const R2 = Math.SQRT1_2;

/** @returns {number[]} the token's 2×2 map as [a, b, c, d]. */
export function opMatrix(opId, value) {
  const op = OPS[opId];
  if (!op) return [1, 0, 0, 1];
  const n = num(value) || 1;
  switch (op.kind) {
    case OpKind.ADD: return [1, n / 10, 0, 1];
    case OpKind.SUB: return [1, -n / 10, 0, 1];
    case OpKind.MUL: return [n, 0, 0, 1];
    case OpKind.DIV: return [1 / n, 0, 0, 1];
    case OpKind.POW: return [n, 0, 0, n];
    case OpKind.ROOT: return [1 / n, 0, 0, 1];
    case OpKind.LOG: return [0.5, 0, 0, 1];
    case OpKind.FACT: return [2, 0, 0, 2];
    case OpKind.SIGMA: return [1, 1, 0, 1];
    case OpKind.INTEGRAL: return [1, 0, 1, 1];
    case OpKind.DERIV: return [1, 0, -1, 1];
    case OpKind.TETRA: return [n, 0, 0, n];
    case OpKind.LIM: return [0, 1, 1, 0];        // reflection, det = -1
    case OpKind.MOD: return [1, 0, 0, -1];       // reflection, det = -1
    case OpKind.ABS: return [1, 0, 0, -1];       // reflection, det = -1
    case OpKind.IMAG: return [0, -1, 1, 0];      // rotation by i, det = +1
    case OpKind.SIN: return [R2, -R2, R2, R2];
    case OpKind.CONST: return [op.constant || 1, 0, 0, 1];
    default: return [1, 0, 0, 1];
  }
}

/** [a,b,c,d] · [a,b,c,d] */
export function matMul(m, n) {
  return [
    m[0] * n[0] + m[1] * n[2], m[0] * n[1] + m[1] * n[3],
    m[2] * n[0] + m[3] * n[2], m[2] * n[1] + m[3] * n[3],
  ];
}

// ------------------------------------------------------------------- rolling

let UID = 1;
export function nextUid() { return UID++; }

/** @typedef {{ opId:string, value:number, uid:number }} Token */

/**
 * Sign lives in the multiplicative group and nowhere else.
 *
 * × and ÷ are the only operators for which a negative operand is *algebra* —
 * two of them cancel, which is the whole point. Everywhere else a negative
 * operand is a landmine that the pipeline currently swallows in silence:
 * `log` base −2 returns exactly 1 with no flag, `^−3` collapses 1e3 to 1e-9,
 * `root` takes a reciprocal degree, and sigma/mod/tetra clamp back to positive
 * without telling anyone. Confine it here, once, and none of those are
 * reachable — from loot, from a save file, or from a hand-edited slot.
 *
 * @param {string} opId
 * @param {number} v
 * @returns {number} `v` for × and ÷, |v| for everything else
 */
export function sanitizeValue(opId, v) {
  const op = OPS[opId];
  const n = num(v);
  if (!op) return n;
  if (n < 0 && op.kind !== OpKind.MUL && op.kind !== OpKind.DIV) return Math.abs(n);
  return n;
}

/** Construct a token by hand (loot tables, save games, the balance sim). */
export function makeToken(opId, value, uid) {
  const op = OPS[opId];
  if (!op) return null;
  const v = value === undefined || value === null
    ? op.rollValue(Math.random, 1)
    : sanitizeValue(opId, value);
  return { opId, value: v, uid: uid === undefined ? nextUid() : uid };
}

/**
 * Tier weighting. Tiers unlock on a schedule and then take over. The decay on
 * tiers I and II is steep on purpose: the balance curve only works if additive
 * and multiplicative drops genuinely dry up around wave 25, forcing the player
 * up the hierarchy instead of stacking ×3 forever.
 */
export function tierWeight(tier, wave, bias = 0) {
  const w = Math.max(0, wave || 0);
  const t = tier + bias * 0.75;
  switch (Math.round(t)) {
    case 1: return Math.max(0.04, 1.6 - w * 0.055);
    case 2: return w < 2 ? 0 : Math.max(0.08, 1.4 - Math.max(0, w - 12) * 0.05);
    case 3: return w < 6 ? 0 : Math.min(1.25, 0.18 + (w - 6) * 0.052);
    case 4: return w < 16 ? 0 : Math.min(1.05, (w - 16) * 0.03);
    case 5: return w < 45 ? 0 : Math.min(1.15, 0.28 + (w - 45) * 0.028);
    default: return t < 1 ? 0.2 : 0.1;
  }
}

/**
 * Roll one operator token.
 * @param {object|Function} rand  core/Rand instance or ()=>[0,1)
 * @param {number} wave
 * @param {number} tierBias  positive pushes the roll up the operator hierarchy
 * @returns {Token}
 */
export function rollToken(rand, wave = 1, tierBias = 0) {
  const w = Math.max(0, wave || 0);
  const pool = [];
  let total = 0;
  for (let i = 0; i < OP_LIST.length; i++) {
    const op = OP_LIST[i];
    if (w < op.minWave) continue;
    const weight = tierWeight(op.tier, w, tierBias) * op.weight;
    if (weight <= 0) continue;
    total += weight;
    pool.push({ op, acc: total });
  }
  if (!pool.length) return makeToken('add', OPS.add.rollValue(rand, w));
  const r = rnd(rand) * total;
  let chosen = pool[pool.length - 1].op;
  for (let i = 0; i < pool.length; i++) {
    if (r <= pool[i].acc) { chosen = pool[i].op; break; }
  }
  return { opId: chosen.id, value: chosen.rollValue(rand, w), uid: nextUid() };
}

/**
 * Apply one token, with a hard guarantee that a BigNum comes out. Formula.js
 * routes every stage through here — one bad token must never poison a run.
 * @returns {BigNum}
 */
export function applyToken(token, x, ctx) {
  if (!token) return big(x);
  const op = OPS[token.opId];
  if (!op) return big(x);
  let out;
  try {
    out = op.apply(big(x), token.value, ctx);
  } catch (err) {
    M(ctx).opError = token.opId;
    return big(x);
  }
  if (!(out instanceof BigNum)) out = BigNum.from(out);
  if (!out.isFinite()) {
    // Only the sanctioned NaN path (0/0) can land here; flag it and keep going.
    M(ctx).undefinedForm = true;
    return big(x);
  }
  return out;
}

/** Product of every equipped token's fire-rate multiplier, clamped to sane bounds. */
export function fireRateOf(tokens) {
  let r = 1;
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    if (!t) continue;
    const op = OPS[t.opId];
    if (op) r *= op.rate;
  }
  return Math.max(0.15, Math.min(3, r));
}

export default OPS;
